// frontend/src/components/RegistrationCodes.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    DeleteIcon,
    PlusIcon as AddIcon,
    KeyIcon, EditIcon, SaveIcon, XCircleIcon,
    ChevronDownIcon,
    UserIcon,
    CopyIcon,
    ShareIcon
} from './Icons';

const RegistrationCodes = () => {
    const { user, API_URL } = useContext(AuthContext);
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newRole, setNewRole] = useState('gruppenleiter');
    const [expiresDays, setExpiresDays] = useState(7);
    const [showPrefill, setShowPrefill] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [copiedLinkId, setCopiedLinkId] = useState(null);
    const [prefillData, setPrefillData] = useState({
        firstName: '',
        lastName: '',
        scoutName: '',
        job: '',
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        phone: '',
        enableEmailRouting: false
    });
    const [editingCodeId, setEditingCodeId] = useState(null);
    const [editData, setEditData] = useState({});

    const fetchCodes = async () => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const { data } = await axios.get(`${API_URL}/admin/codes`, config);
            setCodes(data);
            setLoading(false);
        } catch (err) {
            setError('Fehler beim Laden der Codes');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, API_URL]);

    const generateCode = async () => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const payload = { role: newRole, expiresDays };

            // Only include prefillData if at least one field is filled
            const hasData = Object.values(prefillData).some(v => v.trim() !== '');
            if (hasData) {
                payload.prefillData = prefillData;
            }

            await axios.post(`${API_URL}/admin/generate-code`, payload, config);
            fetchCodes();
            // Reset prefill form
            setPrefillData({ firstName: '', lastName: '', scoutName: '', job: '', street: '', houseNumber: '', zipCode: '', city: '', phone: '', enableEmailRouting: false });
            setShowPrefill(false);
        } catch (err) {
            alert('Fehler beim Generieren des Codes');
        }
    };


    const updateCode = async (id) => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.put(`${API_URL}/admin/codes/${id}`, editData, config);
            setEditingCodeId(null);
            fetchCodes();
        } catch (err) {
            alert('Fehler beim Aktualisieren des Codes');
        }
    };

    const deleteCode = async (id) => {
        if (!window.confirm('Möchten Sie diesen Code wirklich löschen?')) return;
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.delete(`${API_URL}/admin/codes/${id}`, config);
            fetchCodes();
        } catch (err) {
            alert('Fehler beim Löschen des Codes');
        }
    };

    const copyCode = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const copyLink = (code, id) => {
        const registrationLink = `${window.location.origin}/register?code=${code}`;
        navigator.clipboard.writeText(registrationLink);
        setCopiedLinkId(id);
        setTimeout(() => setCopiedLinkId(null), 2000);
    };

    if (loading) return (
        <div className="flex justify-center items-center p-12 bg-surface-containerLow rounded-3xl animate-pulse">
            <div className="md3-progress-circular w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-4 text-surface-onVariant font-medium">Lade Codes...</span>
        </div>
    );

    if (error) return (
        <div className="bg-error-container text-error-on-container p-6 rounded-2xl flex items-center gap-3 shadow-sm border border-error/10">
            <span className="text-xl">⚠️</span>
            <span className="font-bold">{error}</span>
        </div>
    );

    return (
        <div className="flex flex-col gap-10 animate-fade-in">

            {/* Generate New Code Section */}
            <div className="md3-card-outlined p-8 bg-surface-container shadow-sm border border-outlineVariant">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center">
                        <AddIcon sx={{ fontSize: 24 }} />
                    </div>
                    <h3 className="text-title-large font-bold text-surface-on">Neuen Code generieren</h3>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-end">
                    <div className="flex-1 w-full flex flex-col gap-2">
                        <label className="text-label-large text-surface-onVariant ml-1 font-bold">Zielebene / Rolle</label>
                        <div className="relative">
                            <select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                className="md3-textfield-outlined appearance-none cursor-pointer h-14"
                            >
                                <option value="gruppenleiter">Gruppenleiter</option>
                                <option value="admin">Admin</option>
                                <option value="eltern">Eltern</option>
                                <option value="kind">Kind</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex-1 w-full flex flex-col gap-2">
                        <label className="text-label-large text-surface-onVariant ml-1 font-bold">Gültigkeitsdauer (Tage)</label>
                        <input
                            type="number"
                            value={expiresDays}
                            onChange={(e) => setExpiresDays(e.target.value)}
                            min="1"
                            className="md3-textfield-outlined h-14"
                        />
                    </div>

                    <div className="w-full md:w-auto">
                        <button
                            onClick={generateCode}
                            className="md3-btn-filled w-full h-14 shadow-elevation-1 hover:shadow-elevation-2 font-bold px-8"
                        >
                            Generieren
                        </button>
                    </div>
                </div>

                {/* Prefill Data Toggle */}
                <div className="mt-6">
                    <button
                        onClick={() => setShowPrefill(!showPrefill)}
                        className="flex items-center gap-2 text-label-large text-primary font-bold hover:text-primary/80 transition-colors"
                    >
                        <UserIcon size={18} />
                        Profildaten vorausfüllen (optional)
                        <ChevronDownIcon
                            size={18}
                            className={`transition-transform duration-300 ${showPrefill ? 'rotate-180' : ''}`}
                        />
                    </button>

                    <div className={`overflow-hidden transition-all duration-400 ease-out ${showPrefill ? 'max-h-[1200px] opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-surface-containerLow rounded-2xl p-6 border border-outlineVariant/50 space-y-4">
                            <p className="text-body-small text-surface-onVariant mb-2">
                                Diese Daten werden dem Benutzer bei der Registrierung automatisch vorausgefüllt.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Vorname</label>
                                    <input
                                        type="text"
                                        value={prefillData.firstName}
                                        onChange={(e) => setPrefillData({ ...prefillData, firstName: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                        placeholder="z.B. Max"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Nachname</label>
                                    <input
                                        type="text"
                                        value={prefillData.lastName}
                                        onChange={(e) => setPrefillData({ ...prefillData, lastName: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                        placeholder="z.B. Mustermann"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-label-medium text-surface-onVariant ml-1">Pfadiname</label>
                                <input
                                    type="text"
                                    value={prefillData.scoutName}
                                    onChange={(e) => setPrefillData({ ...prefillData, scoutName: e.target.value })}
                                    className="md3-textfield-outlined h-12"
                                    placeholder="z.B. Akela"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1 sm:col-span-2">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Amt / Aufgabe</label>
                                    <input
                                        type="text"
                                        value={prefillData.job}
                                        onChange={(e) => setPrefillData({ ...prefillData, job: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                        placeholder="z.B. Stammesvorstand, Gruppenleiter..."
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Straße</label>
                                    <input
                                        type="text"
                                        value={prefillData.street}
                                        onChange={(e) => setPrefillData({ ...prefillData, street: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Hausnummer</label>
                                    <input
                                        type="text"
                                        value={prefillData.houseNumber}
                                        onChange={(e) => setPrefillData({ ...prefillData, houseNumber: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">PLZ</label>
                                    <input
                                        type="text"
                                        value={prefillData.zipCode}
                                        onChange={(e) => setPrefillData({ ...prefillData, zipCode: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Stadt</label>
                                    <input
                                        type="text"
                                        value={prefillData.city}
                                        onChange={(e) => setPrefillData({ ...prefillData, city: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-label-medium text-surface-onVariant ml-1">Telefon</label>
                                    <input
                                        type="text"
                                        value={prefillData.phone}
                                        onChange={(e) => setPrefillData({ ...prefillData, phone: e.target.value })}
                                        className="md3-textfield-outlined h-12"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-3 p-4 bg-primary-container/10 rounded-2xl border border-primary/20 cursor-pointer hover:bg-primary-container/20 transition-all">
                                        <input
                                            type="checkbox"
                                            checked={prefillData.enableEmailRouting}
                                            onChange={(e) => setPrefillData({ ...prefillData, enableEmailRouting: e.target.checked })}
                                            className="w-5 h-5 rounded-lg border-primary text-primary focus:ring-primary"
                                        />
                                        <div>
                                            <p className="text-label-large font-bold text-surface-on">Automatische Email-Weiterleitung</p>
                                            <p className="text-body-small text-surface-onVariant">Richtet automatisch eine @pfadfinder-wutoeschingen.org Weiterleitung ein.</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Codes Table */}
            <div>
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-secondary-container text-secondary-onContainer flex items-center justify-center">
                        <KeyIcon sx={{ fontSize: 20 }} />
                    </div>
                    <h3 className="text-title-large font-bold text-surface-on">Aktive Codes</h3>
                </div>

                {codes.length === 0 ? (
                    <div className="p-12 text-center bg-surface-containerLow rounded-3xl border-2 border-dashed border-outlineVariant">
                        <p className="text-surface-onVariant italic font-medium text-surface-onVariant">Keine Registrierungscodes im System vorhanden.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-2xl border border-outlineVariant bg-surface-containerHigh shadow-sm">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-surface-containerHighest">
                                <tr>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant">Code</th>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant">Rolle</th>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant hidden md:table-cell">Vorausgefüllt</th>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant">Status</th>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant">Erstellt</th>
                                    <th className="p-5 text-label-medium font-bold text-surface-onVariant uppercase tracking-wider border-b border-outlineVariant text-right">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-outlineVariant">
                                {codes.map((code) => {
                                    const hasPrefill = code.prefillData && Object.values(code.prefillData).some(v => v);
                                    const prefillSummary = hasPrefill
                                        ? [code.prefillData.firstName, code.prefillData.lastName, code.prefillData.scoutName ? `"${code.prefillData.scoutName}"` : ''].filter(Boolean).join(' ')
                                        : '—';

                                    const isEditing = editingCodeId === code._id;
                                    return (
                                        <tr key={code._id} className={`hover:bg-surface-on/[0.04] transition-colors group ${isEditing ? 'bg-primary-container/10' : ''}`}>
                                            <td className="p-5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono font-bold text-lg text-primary bg-primary-container/10 px-3 py-1.5 rounded-lg border border-primary/20">
                                                        {code.code}
                                                    </span>
                                                    <button
                                                        onClick={() => copyCode(code.code, code._id)}
                                                        className="md3-icon-btn text-surface-onVariant hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title="Code kopieren"
                                                    >
                                                        {copiedId === code._id ? '✓' : <CopyIcon size={16} />}
                                                    </button>
                                                    <button
                                                        onClick={() => copyLink(code.code, code._id)}
                                                        className="md3-icon-btn text-surface-onVariant hover:text-tertiary opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title="Anmeldelink kopieren"
                                                    >
                                                        {copiedLinkId === code._id ? '✓' : <ShareIcon size={16} />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="p-5">
                                                {isEditing ? (
                                                    <select
                                                        value={editData.role}
                                                        onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                                                        className="md3-textfield-outlined py-1 h-10 min-w-[120px]"
                                                    >
                                                        <option value="admin">Admin</option>
                                                        <option value="gruppenleiter">Gruppenleiter</option>
                                                        <option value="eltern">Eltern</option>
                                                        <option value="kind">Kind</option>
                                                    </select>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-label-medium font-bold bg-secondary-container text-secondary-onContainer border border-secondary/20">
                                                        {code.role.charAt(0).toUpperCase() + code.role.slice(1)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-5 hidden md:table-cell">
                                                <span className={`text-body-small ${hasPrefill ? 'text-primary font-medium' : 'text-surface-onVariant/50'}`}>
                                                    {prefillSummary}
                                                </span>
                                            </td>
                                            <td className="p-5">
                                                {code.isUsed ? (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-label-small font-bold bg-error-container text-error-on-container shadow-sm">
                                                        <span className="w-2 h-2 rounded-full bg-error animate-pulse"></span>
                                                        Benutzt
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-label-small font-bold bg-primary-container text-primary-onContainer shadow-sm">
                                                        <span className="w-2 h-2 rounded-full bg-primary/80 animate-pulse"></span>
                                                        Aktiv
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-5">
                                                <p className="text-body-medium text-surface-onVariant font-medium">
                                                    {new Date(code.createdAt).toLocaleDateString()}
                                                </p>
                                            </td>
                                            <td className="p-5 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => updateCode(code._id)}
                                                                className="md3-icon-btn text-primary hover:bg-primary-container"
                                                                title="Speichern"
                                                            >
                                                                <SaveIcon sx={{ fontSize: 20 }} />
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingCodeId(null)}
                                                                className="md3-icon-btn text-surface-onVariant hover:bg-surface-containerHighest"
                                                                title="Abbrechen"
                                                            >
                                                                <XCircleIcon sx={{ fontSize: 20 }} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingCodeId(code._id);
                                                                    setEditData({
                                                                        role: code.role,
                                                                        prefillData: { ...code.prefillData }
                                                                    });
                                                                }}
                                                                className="md3-icon-btn text-primary hover:bg-primary-container opacity-0 group-hover:opacity-100 transition-opacity"
                                                                title="Bearbeiten"
                                                            >
                                                                <EditIcon sx={{ fontSize: 20 }} />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteCode(code._id)}
                                                                className="md3-icon-btn text-error hover:bg-error-container hover:text-error-on-container shadow-sm bg-surface-containerHighest"
                                                                title="Löschen"
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 20 }} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationCodes;
